/**
 * stuckInfoFormatter — Pure function fuer Stuck-Info-Zeile auf OrderCard (Phase 12).
 *
 * Liefert deutschen Anzeige-Text aus Notification-Metadaten + Threshold.
 * Format-Beispiele:
 *   "Stuck seit 23h — Unterwegs seit 3d (Schwelle 3d)"
 *   "Stuck seit 47h"                           (kein triggeredEventAt, kein threshold)
 *   "Stuck seit 23h (Schwelle 3d)"             (kein triggeredEventAt)
 *   "Stuck seit 23h — Unterwegs seit 3d"       (kein threshold)
 *
 * Stunden-Format: < 48h → 'Xh', ab 48h → '{Math.floor(h/24)}d'
 *
 * INTERNAL_STATUS_LABELS wird aus dashboardStatsMapper importiert (dort bereits
 * exportiert, kein Duplikat noetig). Konsolidiert beide Verwendungen auf eine Quelle.
 *
 * Kein React/DOM/Fetch-Import — reine TypeScript-Logik, testbar in Vitest environment:node.
 */

export { INTERNAL_STATUS_LABELS } from './dashboardStatsMapper';
import { INTERNAL_STATUS_LABELS } from './dashboardStatsMapper';

export interface StuckInfoInput {
  triggeredAt: Date | string;
  triggeredStatus: string;
  triggeredEventAt: Date | string | null;
  thresholdHours: number | null;
}

/**
 * Formatiert Stuck-Info als deutschen Anzeige-Text.
 *
 * @param input - Notification-Metadaten (triggeredAt, triggeredStatus, triggeredEventAt, thresholdHours)
 * @param now - Referenz-Zeitpunkt (optional, default: new Date()). Injizierbar fuer Test-Determinismus.
 */
// 20.08.2026 (Fix 4): triggeredStatus-Werte, die KEIN interner Tracking-Status
// sind — der Lagerfrist-Alarm kommt aus dem DHL-Rohcode LA/ZF und traegt
// triggeredStatus='lagerfrist' (siehe stuck-check-Flow).
const EXTRA_TRIGGERED_STATUS_LABELS: Record<string, string> = {
  lagerfrist: 'Liegt in Filiale/Packstation',
};

export function formatStuckInfo(input: StuckInfoInput, now: Date = new Date()): string {
  const triggered = toDate(input.triggeredAt);
  const stuckHours = Math.max(0, hoursBetween(triggered, now));
  const label = INTERNAL_STATUS_LABELS[input.triggeredStatus]
    ?? EXTRA_TRIGGERED_STATUS_LABELS[input.triggeredStatus]
    ?? input.triggeredStatus;

  // Phase 15: klares Format — "Im Status 'X' seit Yd · Schwelle Z gerissen vor Wh"
  // Vermeidet die alte Doppelinformation "Stuck seit 40h — Label gedruckt seit 2d (Schwelle 24h)".
  if (input.triggeredEventAt != null) {
    const evt = toDate(input.triggeredEventAt);
    const statusHours = Math.max(0, hoursBetween(evt, now));
    if (input.thresholdHours != null) {
      const overdue = statusHours - input.thresholdHours;
      return `Im Status '${label}' seit ${formatHours(statusHours)} · Schwelle ${formatHours(input.thresholdHours)} gerissen vor ${formatHours(Math.max(0, overdue))}`;
    }
    return `Im Status '${label}' seit ${formatHours(statusHours)}`;
  }

  // Fallback wenn kein triggeredEventAt — alte Logik
  let result = `Stuck seit ${formatHours(stuckHours)}`;
  if (input.thresholdHours != null) {
    result += ` (Schwelle ${formatHours(input.thresholdHours)})`;
  }
  return result;
}

// --- Helpers (nicht exportiert — nur intern) ---

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Differenz in ganzen Stunden (Floor). Negatives Ergebnis moeglich wenn a > b.
 * Caller ist verantwortlich fuer Math.max(0, ...).
 */
function hoursBetween(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / (1000 * 60 * 60));
}

/**
 * Formatiert Stundenzahl (kompakt — wird in DHL-Stuck-Info verwendet):
 * - < 48h → '{h}h'   (z.B. 23h, 47h)
 * - >= 48h → '{d}d'  (z.B. 2d fuer 48-71h, 3d fuer 72-95h)
 */
function formatHours(h: number): string {
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/**
 * Formatiert Stundenzahl in lesbarem Deutsch.
 *
 * Phase 16: praezise "Wochen X Tage"-Anzeige ab 7 Tagen (vorher: nur "X Wochen" gerundet).
 *
 * - <  24h → 'Xh'
 * - <   7 Tage → 'X Tage'       (z.B. '5 Tage', singular '1 Tag')
 * - >=  7 Tage → 'X Wochen Y Tage' (z.B. '1 Woche', '1 Woche 1 Tag', '2 Wochen 4 Tage')
 *   Singular/Plural beachtet, "0 Tage" wird ausgelassen.
 *
 * Beispiele:
 *   23h    -> '23h'
 *   24h    -> '1 Tag'
 *   120h   -> '5 Tage'
 *   168h   -> '1 Woche'
 *   192h   -> '1 Woche 1 Tag'
 *   336h   -> '2 Wochen'
 *   432h   -> '2 Wochen 4 Tage'
 *   1008h  -> '6 Wochen'
 */
export function formatDurationHuman(h: number): string {
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return days === 1 ? '1 Tag' : `${days} Tage`;
  const weeks = Math.floor(days / 7);
  const rem = days - weeks * 7;
  const weekStr = weeks === 1 ? '1 Woche' : `${weeks} Wochen`;
  if (rem === 0) return weekStr;
  const dayStr = rem === 1 ? '1 Tag' : `${rem} Tage`;
  return `${weekStr} ${dayStr}`;
}

// ─── Phase 13: Supplier-Stuck-Info ────────────────────────────────────────

/**
 * Display-Namen pro Lieferant. Sonderfall GlaWel (camelCase) —
 * capitalize('glawel') wuerde 'Glawel' liefern.
 * Map-Keys sind lowercase, Werte sind die Anzeige-Schreibweise.
 *
 * Bewusst lokal dupliziert vom Backend (supplier_status_map) — Frontend
 * darf keine Cross-Module-Imports aus dem Backend ziehen.
 */
export const SUPPLIER_DISPLAY_NAMES: Record<string, string> = {
  huck: 'Huck',
  helo: 'Helo',
  glawel: 'GlaWel',
  sporttore: 'Sporttore',
  wff: 'WFF',          // Phase 15: interner Lieferant
};

/**
 * Formatiert Stuck-Info fuer Lieferanten-Orders (statt DHL-Stati).
 *
 * Format-Beispiele:
 *   "Stuck seit 23h — bei Huck seit 4d (Schwelle 14d)"
 *   "Stuck seit 47h — bei GlaWel (Schwelle 42d)"      (kein triggeredEventAt)
 *   "Stuck seit 23h — bei Sporttore seit 2d"          (kein threshold)
 *   "Stuck seit 0h — bei Helo"                        (nur triggeredAt)
 *
 * Verwendet dieselben internen Helpers (toDate/hoursBetween/formatHours)
 * wie formatStuckInfo — KEINE Duplikate.
 *
 * @param input        - StuckInfoInput (triggeredStatus wird hier nicht verwendet)
 * @param supplierName - Lieferanten-Key (z.B. 'huck', 'helo', 'glawel', 'sporttore').
 *                       Case-insensitive — wird intern auf lowercase normalisiert.
 * @param now          - Referenz-Zeitpunkt (optional, default: new Date())
 */
export function formatSupplierStuckInfo(
  input: StuckInfoInput,
  supplierName: string,
  now: Date = new Date(),
): string {
  const triggered = toDate(input.triggeredAt);
  const stuckHours = Math.max(0, hoursBetween(triggered, now));

  const key = supplierName.toLowerCase();
  const displayName = SUPPLIER_DISPLAY_NAMES[key] ?? supplierName;

  // Phase 15: klares Format — "Bei <Lieferant> seit <Dauer> · Schwelle X gerissen vor Y"
  if (input.triggeredEventAt != null) {
    const evt = toDate(input.triggeredEventAt);
    const statusHours = Math.max(0, hoursBetween(evt, now));
    if (input.thresholdHours != null) {
      const overdue = statusHours - input.thresholdHours;
      return `Bei ${displayName} seit ${formatDurationHuman(statusHours)} · Schwelle ${formatDurationHuman(input.thresholdHours)} gerissen vor ${formatDurationHuman(Math.max(0, overdue))}`;
    }
    return `Bei ${displayName} seit ${formatDurationHuman(statusHours)}`;
  }

  // Fallback ohne triggeredEventAt
  let result = `Stuck seit ${formatDurationHuman(stuckHours)} — bei ${displayName}`;
  if (input.thresholdHours != null) {
    result += ` (Schwelle ${formatDurationHuman(input.thresholdHours)})`;
  }
  return result;
}

/**
 * Lieferanten-Tab: Anzeige fuer Orders OHNE offene Notification.
 * Reine "Wann hat die Order in den Status gewechselt + wie lange liegt sie da"-Zeile.
 *
 * @param supplierStatusSince - ISO-String aus DB-Subquery, oder null
 * @param supplierKey         - 'huck' | 'helo' | 'glawel' | 'sporttore'
 * @param now                 - Referenz-Zeit (Test-Determinismus)
 */
export function formatSupplierStatusSince(
  supplierStatusSince: string | null,
  supplierKey: string,
  now: Date = new Date(),
): string | null {
  if (!supplierStatusSince) return null;
  const since = toDate(supplierStatusSince);
  const hours = Math.max(0, hoursBetween(since, now));
  const key = supplierKey.toLowerCase();
  const displayName = SUPPLIER_DISPLAY_NAMES[key] ?? supplierKey;
  return `Bei ${displayName} seit ${formatDurationHuman(hours)}`;
}
